/**
 * What the Revit Database panel shows — the old `RevitIndexSidebar`'s state
 * ladder, over 2.5's inputs (item BK): the plugin's status file, whether the
 * context loaded, and what the user is looking at. Pure, so the order of the
 * rungs is testable; the order is the meaning.
 *
 *   building → the plugin is indexing right now (the one cost to Revit, on the
 *              user's click); nothing else is worth showing
 *   loading  → this panel is fetching
 *   failed   → the last build failed; retry
 *   unavailable → no document is open in that Revit
 *   missing  → a document is open and no index has been built — Build index —
 *              or the context simply cannot be read
 *   rows / types → the index, with the stale banner on top when the plugin
 *              says the document changed since the build
 */
import type { DocumentContextStatus } from "./documentContext";

export type IndexViewKind = "building" | "loading" | "failed" | "unavailable" | "missing" | "rows" | "types";

export interface IndexView {
  kind: IndexViewKind;
  /** The document changed since the build: show the banner, offer refresh. */
  stale: boolean;
}

export function resolveIndexView(input: {
  status: DocumentContextStatus | null;
  keysLoaded: boolean;
  keyCount: number;
  loading: boolean;
  showRows: boolean;
  /** A rebuild was asked for and has not settled — building, whatever the last status said. */
  rebuilding?: boolean;
}): IndexView {
  const state = input.status?.state ?? "unknown";
  const stale = state === "stale";
  if (state === "building" || input.rebuilding) return { kind: "building", stale: false };
  if (input.loading) return { kind: "loading", stale };
  if (state === "failed") return { kind: "failed", stale: false };
  if (state === "unavailable") return { kind: "unavailable", stale: false };
  if (state === "missing") return { kind: "missing", stale: false };
  if (!input.keysLoaded || input.keyCount === 0) return { kind: "missing", stale: false };
  return { kind: input.showRows ? "rows" : "types", stale };
}

/**
 * Build the index **once** for an instance whose plugin reports `missing` — the
 * first time a Revit is selected (or its document changes), not on every poll,
 * and never when the plugin cannot rebuild or is already building. The key
 * names what was built for, so a new document (a new generation) builds again
 * and the same one does not.
 */
export function autoBuildKey(status: DocumentContextStatus | null, targetSessionId: string | null): string | null {
  if (!status || !targetSessionId) return null;
  if (status.state !== "missing" || !status.canRebuild) return null;
  return `${targetSessionId}:${status.generation ?? "?"}`;
}

/**
 * Whether the index is still on its way: no document yet, nothing built, a build
 * running, or no status file yet (a plugin that has not published one). Keep
 * reading the status until it settles, so a model that finishes opening after
 * the instance was selected still reaches `missing` and its one auto-build.
 */
export function shouldPollStatus(status: DocumentContextStatus | null): boolean {
  const state = status?.state ?? "unknown";
  return state === "unknown" || state === "unavailable" || state === "missing" || state === "building";
}
